import React, { useEffect, useRef, useState } from "react";
import { ClassicEditor, Essentials, Paragraph } from "ckeditor5";
import MathType from "@wiris/mathtype-ckeditor5/dist/index.js";
import { useLocale } from "../../../locales";

import "ckeditor5/ckeditor5.css";

interface MathTypeEditorModalProps {
  isOpen: boolean;
  initialValue?: string;
  onClose: () => void;
  onSave: (mathml: string) => void;
}

const MathTypeEditorModal: React.FC<MathTypeEditorModalProps> = ({
  isOpen,
  initialValue = "",
  onClose,
  onSave,
}) => {
  const { t } = useLocale();
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const editorInstanceRef = useRef<any>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);

  const patchWirisPrototypeKeydown = () => {
    const wiris = (window as any).WirisPlugin;
    const contentManager = wiris?.currentInstance?.core?.contentManager;
    const proto = contentManager?.constructor?.prototype as
      | { onKeyDown?: (event: KeyboardEvent) => void; __qePatched?: boolean }
      | undefined;
    if (!proto || proto.__qePatched || !proto.onKeyDown) {
      return;
    }
    const original = proto.onKeyDown;
    proto.onKeyDown = function patchedOnKeyDown(keyboardEvent: KeyboardEvent) {
      const formulaDisplay = document.getElementsByClassName(
        "wrs_formulaDisplay"
      )[0];
      if (!formulaDisplay) {
        return;
      }
      return original.call(this, keyboardEvent);
    };
    proto.__qePatched = true;
  };

  const patchWirisModalKeydown = () => {
    const wiris = (window as any).WirisPlugin;
    const contentManager = wiris?.currentInstance?.core?.contentManager as
      | { onKeyDown?: (event: KeyboardEvent) => void; __qePatched?: boolean }
      | undefined;
    if (!contentManager || contentManager.__qePatched) {
      return;
    }
    const original = contentManager.onKeyDown?.bind(contentManager);
    if (!original) {
      return;
    }
    contentManager.onKeyDown = (keyboardEvent: KeyboardEvent) => {
      const formulaDisplay = document.getElementsByClassName(
        "wrs_formulaDisplay"
      )[0];
      if (!formulaDisplay) {
        return;
      }
      original(keyboardEvent);
    };
    contentManager.__qePatched = true;
  };

  useEffect(() => {
    if (!isOpen) return;
    const intervalId = window.setInterval(() => {
      patchWirisPrototypeKeydown();
      patchWirisModalKeydown();
    }, 200);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const modalOpen =
        document.getElementsByClassName("wrs_modal_dialogContainer").length > 0;
      if (!modalOpen) return;
      const formulaDisplay = document.getElementsByClassName(
        "wrs_formulaDisplay"
      )[0];
      if (formulaDisplay) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen]);

  // Initialize CKEditor with MathType plugin
  useEffect(() => {
    if (!isOpen || !editorContainerRef.current) return;

    const initializeEditor = async () => {
      try {
        // Destroy previous instance if exists
        if (editorInstanceRef.current) {
          await editorInstanceRef.current.destroy();
          editorInstanceRef.current = null;
        }

        // Suppress CKEditor telemetry errors
        const originalConsoleError = console.error;
        console.error = (...args: any[]) => {
          const hasTelemetryError = args.some((arg) => {
            if (!arg) return false;
            const message = arg?.message ? String(arg.message) : String(arg);
            const lower = message.toLowerCase();
            return lower.includes('send events batch') || lower.includes('forcing send events');
          });
          if (hasTelemetryError) return; // Suppress telemetry errors
          originalConsoleError.apply(console, args);
        };

        // Create new editor instance
        const editor = await ClassicEditor.create(editorContainerRef.current!, {
          licenseKey: 'GPL', // GPL lisansı için
          plugins: [Essentials, Paragraph, MathType],
          toolbar: {
            items: ["MathType", "ChemType", "|", "undo", "redo"],
          },
          initialData: initialValue || "",
        } as any); // Type assertion to allow MathType configuration

        // Restore console.error
        console.error = originalConsoleError;

        editorInstanceRef.current = editor;
        setIsEditorReady(true);
        patchWirisModalKeydown();

        // Add error listener to suppress telemetry errors
        editor.on('error', (evt: any, error: any) => {
          const message = error?.message ? String(error.message).toLowerCase() : '';
          if (message.includes('send events batch') || message.includes('forcing send events')) {
            evt.stop();
            return;
          }
        });

        // Focus editor
        try {
          editor.editing.view.focus();
        } catch (err) {
          // Suppress focus errors
          console.log('Editor focus error (suppressed):', err);
        }
      } catch (error) {
        console.error("CKEditor initialization error:", error);
      }
    };

    initializeEditor();

    // Cleanup on unmount
    return () => {
      if (editorInstanceRef.current) {
        editorInstanceRef.current
          .destroy()
          .catch((err: unknown) =>
            console.error("CKEditor destroy error:", err)
          );
        editorInstanceRef.current = null;
      }
      setIsEditorReady(false);
    };
  }, [isOpen, initialValue]);

  const handleSave = () => {
    if (editorInstanceRef.current) {
      const data = editorInstanceRef.current.getData();
      // Extract only <math> elements from CKEditor output.
      // CKEditor wraps content in <p> tags and may include <br>, &nbsp;, etc.
      // which interfere with MathML rendering on canvas.
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = data;
      const mathElements = tempDiv.querySelectorAll("math");
      if (mathElements.length > 0) {
        const cleanMathml = Array.from(mathElements)
          .map((el) => el.outerHTML)
          .join("");
        onSave(cleanMathml);
      } else {
        onSave(data);
      }
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="qe-modal-overlay">
      <div className="qe-modal-content" style={{ maxWidth: "900px" }}>
        <div className="qe-modal-header">
          <h3>{t("mathEditor.title")}</h3>
          <button className="qe-modal-close" onClick={handleCancel}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="qe-modal-body">
          <div className="qe-form__field">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <span className="qe-form__label">{t("mathEditor.expression")}</span>
              {!isEditorReady && (
                <span style={{ fontSize: "12px", color: "#6b7280" }}>
                  {t("mathEditor.loading")}
                </span>
              )}
            </div>
            <div
              ref={editorContainerRef}
              style={{
                minHeight: "300px",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
              }}
            />
          </div>
        </div>

        <div className="qe-modal-footer">
          <button
            className="qe-button qe-button--secondary"
            onClick={handleCancel}
          >
            {t("mathEditor.cancel")}
          </button>
          <button
            className="qe-button qe-button--primary"
            onClick={handleSave}
            disabled={!isEditorReady}
          >
            {t("mathEditor.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MathTypeEditorModal;
